import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Html5QrcodeScanner } from 'html5-qrcode';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

interface QRCodeItem {
  code: string;
  location: string;
  qrImageUrl?: string;  // Format: /api/vendors/{vendorId}/qr/{code}/image
  vendorId?: number;
  vendorName?: string;
  vendorUsername?: string;
}

/** How many demo QR codes to render. The list is a convenience for testing,
 *  not a browsable directory — see the comment at the render site. */
const QR_DEMO_LIST_LIMIT = 25;

const QR: React.FC = () => { // Typed as React.FC
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [qrList, setQrList] = useState<QRCodeItem[]>([]);
  const [loadingQrList, setLoadingQrList] = useState<boolean>(true);
  const navigate = useNavigate();
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const scannerContainerId = "qr-reader-container-unique"; // Ensure unique ID for scanner
  const { theme } = useTheme();
  const { clearCart, clearVendorContext } = useCart();
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const { user } = useAuth();

  const handleNavigation = async (scannedCode: string) => {
    setError(null); // Clear previous errors
    try {
      // Clear any existing vendor context before navigating
      await clearVendorContext();
      // Clear cart explicitly as well
      await clearCart();
      // API returns MenuWithTableResponse object
      console.log("Making API request for QR code:", scannedCode);
      const response = await api.get<{
        vendorId: number;
        vendorName: string;
        vendorLogo?: string;
        tableId?: number;
        tableName?: string;
        qrCode: string;
        categories: Array<any>;
        whatsappEnabled?: boolean;
        whatsappNumber?: string;
      }>(`/qr/${scannedCode}/menu`);
      const menuData = response.data;
      if (menuData && menuData.vendorId) {
        console.log("QR navigation successful, going to menu:", menuData.vendorId);

        // Check if vendor has WhatsApp ordering enabled
        if (menuData.whatsappEnabled && menuData.whatsappNumber) {
          // Check if customer already made a channel choice for this session
          const SESSION_KEY = 'sog_channel_choice';
          let skipChannelChoice = false;
          try {
            const stored = sessionStorage.getItem(SESSION_KEY);
            if (stored) {
              const choice = JSON.parse(stored);
              const twoHours = 2 * 60 * 60 * 1000;
              if (
                choice.vendorId === String(menuData.vendorId) &&
                choice.qrCode === scannedCode &&
                Date.now() - choice.timestamp < twoHours &&
                choice.choice === 'browser'
              ) {
                skipChannelChoice = true;
              }
            }
          } catch {
            // Ignore parse errors
          }

          if (!skipChannelChoice) {
            console.log("WhatsApp ordering enabled, showing channel choice page");
            const channelParams = new URLSearchParams({
              vendorId: menuData.vendorId.toString(),
              qrCode: scannedCode,
              vendorName: menuData.vendorName || '',
              tableName: menuData.tableName || '',
              whatsappNumber: menuData.whatsappNumber,
            });
            if (menuData.vendorLogo) {
              channelParams.append('vendorLogo', menuData.vendorLogo);
            }
            navigate(`/channel-choice?${channelParams.toString()}`);
            return;
          }
        }

        // Include table info in navigation if available
        const queryParams = new URLSearchParams({ qr: scannedCode });
        if (menuData.tableId) {
          queryParams.append('tableId', menuData.tableId.toString());
        }
        navigate(`/menu/${menuData.vendorId}?${queryParams.toString()}`);
      } else {
        console.error("No menu found for QR code:", scannedCode);
        setError('No menu found for this QR code or QR code data is invalid.');
      }
    } catch (err) {
      console.error("QR navigation failed:", err);
      setError(err.response?.data?.message || err.message || "Failed to process QR code.");
    }
  };

  useEffect(() => {
    // Only initialize scanner if the container element exists
    const containerElement = document.getElementById(scannerContainerId);
    if (!containerElement) {
      console.log("QR Scanner container not found");
      return;
    }

    const config = { 
      fps: 5, // Reduced FPS to prevent overwhelming errors
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1,
      disableFlip: false,
      supportedScanTypes: [0, 1] // QR_CODE and DATA_MATRIX
    };
    
    // Ensure no existing scanner instance is active on this element.
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error);
      scannerRef.current = null;
    }

    const newScanner = new Html5QrcodeScanner(scannerContainerId, config, false);
    scannerRef.current = newScanner;

    try {
      newScanner.render(
        async (decodedText) => {
          if (isScanning) {
            console.log("Already processing QR code, ignoring duplicate detection");
            return; // Prevent multiple scans
          }
          setIsScanning(true);
          setCode(decodedText); // Update state with scanned code
          console.log("QR Code detected:", decodedText);
          
          // Stop scanner completely before navigation
          try {
            if (scannerRef.current) {
              console.log("Stopping QR scanner...");
              await scannerRef.current.clear();
              scannerRef.current = null;
              console.log("QR scanner stopped successfully");
            }
          } catch (clearError) {
            console.warn("Error clearing QR scanner after detection:", clearError);
          }
          
          // Small delay to ensure scanner is fully stopped
          setTimeout(async () => {
            await handleNavigation(decodedText);
            setIsScanning(false);
          }, 100);
        },
        (errorMessage) => {
          // Filter out common expected errors that occur during normal scanning
          const isExpectedError = errorMessage.includes('No barcode or QR code detected') ||
                                  errorMessage.includes('No MultiFormat Readers were able to detect the code') ||
                                  errorMessage.includes('NotReadableError') ||
                                  errorMessage.includes('QR code parse error');
          
          // Only log initialization and serious errors, not scanning errors
          if (!isExpectedError && scannerRef.current) {
            console.warn("QR scanner error:", errorMessage);
          }
        }
      );
    } catch (renderError: any) {
      // This catches errors during the scanner's render method itself.
      console.error("QR Scanner render error:", renderError);
      setError("Failed to initialize QR scanner. Please ensure camera permissions are enabled and try refreshing the page.");
    }
    
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(err => {
          // Catch error if scanner already cleared or not initialized.
          console.warn("Error clearing scanner:", err);
        });
        scannerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [scannerContainerId]); // Rerun if containerId or isScanning changes, though scannerContainerId is constant here.

  // IMPORTANT: Always clear cart and vendor context when QR page mounts
  // This prevents the app from getting stuck in a navigation loop
  useEffect(() => {
    // Reset cart and vendor state to prevent lingering data
    const clearContext = async () => {
      console.log('QR page mounted - FORCE CLEARING vendor context');
      
      // Clear immediately from localStorage
      localStorage.removeItem('vendorId');
      localStorage.removeItem('qrCode');
      // Clear all previousPath namespaces since QR is customer-side entry
      localStorage.removeItem('previousPath:CUSTOMER');
      localStorage.removeItem('previousPath:VENDOR');
      localStorage.removeItem('previousPath:STEWARD');
      // Clear role-scoped navigation stacks
      sessionStorage.removeItem('navStack:CUSTOMER');
      sessionStorage.removeItem('navStack:VENDOR');
      sessionStorage.removeItem('navStack:STEWARD');
      sessionStorage.removeItem('navBack:CUSTOMER');
      sessionStorage.removeItem('navBack:VENDOR');
      sessionStorage.removeItem('navBack:STEWARD');
      
      // Then clear through context
      await clearVendorContext();
      
      // Verify it's cleared
      console.log('QR page context cleared, localStorage state:', {
        vendorId: localStorage.getItem('vendorId'),
        qrCode: localStorage.getItem('qrCode')
      });
    };
    
    // Execute immediately
    clearContext();
  }, [clearVendorContext]);

  useEffect(() => {
    setLoadingQrList(true);
    api.get<QRCodeItem[]>('/qr')
      .then(res => {
        setQrList(res.data);
        setLoadingQrList(false);
      })
      .catch(() => {
        setError("Could not load available QR codes."); // Inform user about this specific error
        setLoadingQrList(false);
      });
  }, []);

  useEffect(() => {
    // Guard browser back behavior on QR page: if user hits back from QR, send to safe entry
    const handlePopState = () => {
      // Route based on current role zone
      const roles = user?.roles || [];
      const isVendor = roles.includes('ROLE_VENDOR');
      const isSteward = roles.includes('ROLE_STEWARD');
      const safeHome = isSteward ? '/steward/dashboard' : isVendor ? '/vendor/orders' : '/';
      navigate(safeHome, { replace: true });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, user?.roles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      handleNavigation(code.trim());
    } else {
      setError('Please enter a valid QR code.');
    }
  };

  const renderQRPageContent = () => (
    <div 
      className="w-full max-w-lg mx-auto shadow-xl rounded-2xl p-6 md:p-8 space-y-6"
      style={{ backgroundColor: theme.background, color: theme.text }}
    >
      {/* QR Scanner Section */}
      <div id={scannerContainerId} className="w-full aspect-square max-w-xs mx-auto border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden" />
      
      {/* Manual Code Input Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="manualQrCode" className="block text-sm font-medium mb-1" style={{ color: theme.text }}>
            Or Enter Code Manually:
          </label>
          <input
            id="manualQrCode"
            type="text"
            className="appearance-none block w-full px-4 py-2.5 border rounded-lg shadow-sm sm:text-sm transition-colors"
            style={{
              backgroundColor: theme.components.input.background,
              borderColor: theme.components.input.border,
              borderWidth: '1px',
              color: theme.text,
            }}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter QR Code"
          />
        </div>

        {error && (
          <p className="p-3 text-sm rounded-lg" style={{ 
            backgroundColor: 'var(--status-bad-bg)', 
            color: 'var(--status-bad-text)',
            border: '1px solid #FFCDD2' /* basil-todo */
          }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium"
          style={{
            backgroundColor: theme?.button?.primary?.bg || theme?.buttons?.primary || '#B37A4C',
            color: theme?.button?.primary?.text || theme?.text?.onPrimary || '#FFFFFF',
          }}
        >
          Submit Code
        </button>
      </form>

      {/* Available QR Codes Section (for testing/demo) */}
      {Array.isArray(qrList) && qrList.length > 0 && (
        <section className="pt-6 border-t" style={{ borderColor: theme.components.card.border }}>
          <h3 className="text-lg font-semibold mb-3" style={{ color: theme?.text?.heading || theme?.text?.primary || theme?.primary }}>Available QR Codes (for Demo)</h3>
          {loadingQrList ? <p className="text-sm" style={{ color: theme.text }}>Loading list...</p> : (
            <ul className="space-y-2">
              {/* Capped: this convenience list rendered EVERY QR the API returns.
                  On a populated database that is hundreds of rows — measured at
                  245,874px tall / 14,667 DOM nodes, which no one scrolls and
                  which pushed a full-page screenshot past 9s (visual-coverage
                  needs two consecutive stable captures inside 15s, so /qr and
                  /menu — which redirects here — timed out at every width).
                  Manual code entry above remains the real entry point. */}
              {qrList.slice(0, QR_DEMO_LIST_LIMIT).map(qr => {
                // Use vendor information from API response
                let vendorDisplay = '';
                
                if (qr.vendorName) {
                  // Prefer vendor name if available
                  vendorDisplay = ` - ${qr.vendorName}`;
                } else if (qr.vendorUsername) {
                  // Fall back to username and format it nicely
                  const formatted = qr.vendorUsername
                    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
                    .trim();
                  vendorDisplay = ` - ${formatted}`;
                } else if (qr.vendorId) {
                  // Last resort: show vendor ID
                  vendorDisplay = ` - Vendor ${qr.vendorId}`;
                }
                
                return (
                  <li key={qr.code} className="p-3 rounded-md shadow-sm text-sm" style={{ backgroundColor: theme?.components?.card?.background || '#FFFFFF' }}>
                    <button 
                      onClick={() => { setCode(qr.code); handleNavigation(qr.code); }}
                      className="focus:outline-none"
                      title={`Use code ${qr.code}`}
                    >
                      <span
                        style={{
                          backgroundColor: theme?.components?.badge?.background || theme?.button?.secondary?.bg || '#F8EEDC',
                          color: theme?.components?.badge?.text || theme?.button?.secondary?.text || '#3E3E3E',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        QR–{qr.code}
                      </span>
                    </button>
                    <span style={{ color: theme?.text?.secondary || theme?.text }}>
                      {vendorDisplay}
                      {qr.location && <>, <span style={{ color: theme?.text?.label || theme?.text?.secondary }}>Location:</span> {qr.location}</>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {!loadingQrList && qrList.length > QR_DEMO_LIST_LIMIT && (
            <p className="text-sm mt-3" style={{ color: theme?.text?.secondary || theme?.text }}>
              Showing {QR_DEMO_LIST_LIMIT} of {qrList.length}. Enter a code above to use one that isn&apos;t listed.
            </p>
          )}
        </section>
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor: theme.background, color: theme.text }}>
      <CardLayoutTheme
        title="QR Code Management"
        categories={[{ id: 1, name: 'QR Operations' }]} // Dummy category
        renderItems={renderQRPageContent}
        sidebarTitle="QR Code Status"
      />
    </div>
  );
};

export default QR;
